/* eslint-disable react/require-default-props */
import React, {
  Fragment, useRef, useState, useMemo,
} from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, ArrowPathIcon, LinkIcon } from '@heroicons/react/20/solid';
import { useLinkEnrichedLeadMutation, useGetAllCorporationsQuery } from './enrichedLeadsAPI';
import { EnrichedLeadCompany } from './enriched-leads-types';

interface EnrichedLeadLinkCorporationModalProps {
  show: boolean;
  setOpen: (flag: boolean) => void;
  company: EnrichedLeadCompany;
}

export default function EnrichedLeadLinkCorporationModal({ show, setOpen, company }: EnrichedLeadLinkCorporationModalProps) {
  const cancelButtonRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCorporationId, setSelectedCorporationId] = useState<number | null>(null);

  const { data: allCorporations, isLoading: isLoadingCorps } = useGetAllCorporationsQuery(undefined, { skip: !show });
  const [linkCompany, { isLoading: isLinking }] = useLinkEnrichedLeadMutation();

  const filteredCorporations = useMemo(() => {
    if (!allCorporations || searchValue.trim().length < 2) return [];
    const search = searchValue.trim().toLowerCase();
    return allCorporations
      .filter((corp) => corp.name.toLowerCase().includes(search))
      .slice(0, 15);
  }, [allCorporations, searchValue]);

  const handleLink = async () => {
    if (selectedCorporationId === null) return;
    await linkCompany({ enrichedLeadCompanyId: company.id, corporationId: selectedCorporationId });
    setOpen(false);
    setSearchValue('');
    setSelectedCorporationId(null);
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-600 opacity-80" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block relative z-20 align-bottom bg-white rounded-lg px-8 pt-6 pb-6 text-left overflow-hidden shadow-lg transition-all sm:align-middle sm:max-w-lg sm:w-full">
              <Dialog.Title as="h3" className="text-lg font-bold text-neutral-800 mb-1">
                Link to Corporation
              </Dialog.Title>
              <p className="text-sm text-neutral-500 mb-4">
                Search for a registered corporation to link with
                {' '}
                <span className="font-semibold text-neutral-700">{company.name}</span>
              </p>
              <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2 mb-4 leading-relaxed">
                Each corporation can only be linked to one enriched lead. After you sync profile data, unlinking
                {' '}
                <span className="font-semibold">rolls back</span>
                {' '}
                the corporation profile for the field groups you synced (restored to their state before the first sync of each group).
              </p>

              <div className="relative">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    setSelectedCorporationId(null);
                  }}
                  placeholder="Type at least 2 characters to search..."
                  className="w-full border border-neutral-300 rounded-full px-4 py-2.5 pr-10 text-sm font-semibold text-neutral-900 placeholder-neutral-500 focus:ring-1 focus:ring-main-600 focus:border-main-600 outline-none"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  {isLoadingCorps ? (
                    <ArrowPathIcon className="h-4 w-4 text-neutral-400 animate-spin" />
                  ) : (
                    <MagnifyingGlassIcon className="h-4 w-4 text-neutral-400" />
                  )}
                </div>
              </div>

              {filteredCorporations.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto border border-neutral-200 rounded-md">
                  {filteredCorporations.map((corp) => (
                    <button
                      key={corp.id}
                      type="button"
                      onClick={() => setSelectedCorporationId(corp.id)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-neutral-100 last:border-b-0 ${
                        selectedCorporationId === corp.id
                          ? 'bg-main-50 text-main-700 font-semibold'
                          : 'hover:bg-neutral-25 text-neutral-700'
                      }`}
                    >
                      <span className="font-semibold">{corp.name}</span>
                      <span className="text-xs text-neutral-400 ml-2">
                        {'ID: '}
                        {corp.id}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {searchValue.trim().length >= 2 && filteredCorporations.length === 0 && !isLoadingCorps && (
                <p className="mt-3 text-sm text-neutral-500 text-center py-4">No corporations found</p>
              )}

              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center rounded-md border border-neutral-300 py-2.5 px-5 bg-white text-sm font-semibold text-main-600 hover:bg-neutral-25 focus:outline-none transition-colors"
                  onClick={() => {
                    setOpen(false);
                    setSearchValue('');
                    setSelectedCorporationId(null);
                  }}
                  ref={cancelButtonRef}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={selectedCorporationId === null || isLinking}
                  onClick={handleLink}
                  className={`inline-flex items-center py-2.5 px-5 rounded-md border border-transparent text-sm font-semibold text-white transition-colors ${
                    selectedCorporationId === null || isLinking
                      ? 'bg-main-300 cursor-not-allowed'
                      : 'bg-main-600 hover:bg-main-700'
                  }`}
                >
                  {isLinking ? (
                    <ArrowPathIcon className="h-4 w-4 text-white mr-2 animate-spin" />
                  ) : (
                    <LinkIcon className="h-4 w-4 text-white mr-2" />
                  )}
                  Confirm Link
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
